import myAxios from "./axiositeachWrapper";
import Config from "../_config/config";

// ==================== Types ====================

export interface LookupItem {
  id: number;
  name: string;
}

export interface TestStructureCategory {
  id: number;
  name: string;
  sortOrder: number | null;
  sourceCategoriesForConsolidation?: boolean;
  computeSgpa?: boolean;
  reallocateMarks?: boolean;
  computeStudentGrade?: boolean;
  graceMarks?: boolean;
  trackDataEntry?: boolean;
  deleteTests?: boolean;
}

export interface TestStructureCell {
  testId: number;
  testName: string;
  categoryId: number;
  categoryName: string | null;
  isBacklog: boolean;
  testDate: string | null;
  totalMarks: number | null;
  totalQuestions: number | null;
  publishStatusId: number | null;
  publishStatus: string | null;
  attempted: number;
  denominator: number;
  coverage: string;
  courseId: number;
  courseName: string | null;
  termId: number;
}

export interface TestStructureCourse {
  id: number;
  name: string;
  code: string | null;
  cells: Record<string, TestStructureCell[]>;
}

export interface TestStructureTerm {
  id: number;
  name: string;
  displayName: string | null;
  totalStudents: number;
  courses: TestStructureCourse[];
}

export interface TestStructureResponse {
  program: { id: number; name: string };
  year: { id: number; name: string };
  categories: TestStructureCategory[];
  terms: TestStructureTerm[];
  /**
   * Per-term map of categories that have a consolidation rule configured
   * via the Merge/Consolidation tab (stored in program_term_year_properties).
   * Keyed by termId, value is the list of categoryIds that are merge targets
   * within that term.
   */
  consolidationByTerm: Record<number, number[]>;
  unmappedTests: TestStructureCell[];
}

// ==================== Service ====================

class TestStructureService {
  private baseUrl = `${Config.nodeSiteUrl}v1/test-structure`;

  /** Course types (student_enquiry_type2_master) — loaded once on mount */
  async getCourseTypes(): Promise<LookupItem[]> {
    const response = await myAxios.get(`${this.baseUrl}/course-types`);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (response.data as any)?.data || [];
  }

  /**
   * Programs filtered by enquiry type 2 (mirrors Java
   * `enrollmentSelect.view?populate=programsForProgramType`).
   */
  async getPrograms(enquiryType2Id?: number | null): Promise<LookupItem[]> {
    const params: Record<string, number> = {};
    if (enquiryType2Id) params.enquiryType2Id = enquiryType2Id;
    const response = await myAxios.get(`${this.baseUrl}/programs`, { params });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (response.data as any)?.data || [];
  }

  /**
   * Years open for a program's test structure (mirrors Java
   * `enrollmentSelect.view?populate=yearsForProgram&openForTestStructure=1`).
   */
  async getYears(programId: number): Promise<LookupItem[]> {
    const response = await myAxios.get(`${this.baseUrl}/years`, {
      params: { programId },
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (response.data as any)?.data || [];
  }

  /** Full matrix for a program+year */
  async getStructure(programId: number, yearId: number): Promise<TestStructureResponse> {
    const response = await myAxios.get(this.baseUrl, {
      params: { programId, yearId },
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (response.data as any)?.data;
  }

  // ── Program Level Publish ──

  async getPublishStatus(programId: number, yearId: number) {
    const resp = await myAxios.get(`${this.baseUrl}/publish-status`, { params: { programId, yearId } });
    return (resp.data as any)?.data;
  }

  async programPublish(programId: number, yearId: number, publishType: 1 | 2) {
    const resp = await myAxios.post(`${this.baseUrl}/program-publish`, { programId, yearId, publishType });
    return resp.data as any;
  }

  // ── Borrow Test Rules (clone Config from another year) ──

  async getBorrowYears(programId: number, yearId: number) {
    const resp = await myAxios.get(`${this.baseUrl}/borrow-years`, { params: { programId, yearId } });
    return (resp.data as any)?.data?.years || [];
  }

  async applyBorrowedRules(programId: number, yearId: number, cloneYearId: number) {
    const resp = await myAxios.post(`${this.baseUrl}/borrow-rules`, { programId, yearId, cloneYearId });
    return resp.data as any;
  }

  // ── Category Config (Settings — sourceCategoriesForConsolidation) ──

  async getCatConfig(programId: number, yearId: number, termId: number, categoryId: number) {
    const resp = await myAxios.get(`${this.baseUrl}/cat-config`, { params: { programId, yearId, termId, categoryId } });
    return (resp.data as any)?.data;
  }

  async saveCatConfig(programId: number, yearId: number, termId: number, categoryId: number, sourceCategoriesForConsolidation: any) {
    const resp = await myAxios.post(`${this.baseUrl}/cat-config/save`, { programId, yearId, termId, categoryId, sourceCategoriesForConsolidation });
    return resp.data as any;
  }

  // ── Pass Marks (threshold) ──

  async getTestsWithThreshold(programId: number, yearId: number, termId: number, categoryId: number) {
    const resp = await myAxios.get(`${this.baseUrl}/tests-with-threshold`, { params: { programId, yearId, termId, categoryId } });
    return (resp.data as any)?.data;
  }

  async updateThreshold(tests: Array<{ sections: Array<{ sectionId: number; threshold: number; maxMarks: number }> }>) {
    const resp = await myAxios.post(`${this.baseUrl}/update-threshold`, { tests });
    return resp.data as any;
  }

  // ── Reallocate Marks ──

  async reallocateMarks(programId: number, termId: number, categoryId: number, yearId: number) {
    const resp = await myAxios.post(`${this.baseUrl}/reallocate`, { programId, termId, categoryId, yearId });
    return resp.data as any;
  }

  // ── Month and Year of Passing ──

  async computeMonthAndYearOfPassing(programId: number, termId: number, categoryId: number, yearId: number) {
    const resp = await myAxios.post(`${this.baseUrl}/month-year-passing`, { programId, termId, categoryId, yearId });
    return resp.data as any;
  }

  // ── Compute Grade / SGPA / CGPA / Publish Report ──

  async getGradeBoundaries(programId: number, yearId: number, termId: number, categoryId: number, typeId: number) {
    const resp = await myAxios.get(`${this.baseUrl}/grade-boundaries`, { params: { programId, yearId, termId, categoryId, typeId } });
    return (resp.data as any)?.data;
  }

  async computeGrade(programId: number, termId: number, categoryId: number, yearId: number, typeId: number, eraseGradeBoundaries?: boolean) {
    const resp = await myAxios.post(`${this.baseUrl}/compute-grade`, { programId, termId, categoryId, yearId, typeId, eraseGradeBoundaries });
    return resp.data as any;
  }

  async computeSgpa(programId: number, termId: number, categoryId: number, yearId: number) {
    const resp = await myAxios.post(`${this.baseUrl}/compute-sgpa`, { programId, semesterId: termId, categoryId, yearId });
    return resp.data as any;
  }

  async computeCgpa(programId: number, termId: number, yearId: number) {
    const resp = await myAxios.post(`${this.baseUrl}/compute-cgpa`, { programId, semesterId: termId, yearId });
    return resp.data as any;
  }

  async getPublishReportStatus(programId: number, termId: number, yearId: number) {
    const resp = await myAxios.get(`${this.baseUrl}/publish-report-status`, { params: { programId, termId, yearId } });
    return (resp.data as any)?.data;
  }

  async publishReport(programId: number, termId: number, yearId: number, publishType: number) {
    const resp = await myAxios.post(`${this.baseUrl}/publish-report`, { programId, semesterId: termId, yearId, publishType });
    return resp.data as any;
  }

  // ── Test Merge (consolidation) ──

  async mergeTest(programId: number, courseId: number, termId: number, categoryId: number, yearId: number, action: string) {
    const resp = await myAxios.post(`${this.baseUrl}/test-merge`, { programId, courseId, termId, categoryId, yearId, action });
    return resp.data as any;
  }

  // ── Exam Dates ──

  async getExamDates(programId: number, yearId: number, termId: number) {
    const resp = await myAxios.post(`${this.baseUrl}/exam-dates`, { programId, yearId, termId });
    return (resp.data as any)?.data;
  }

  async saveExamDates(rows: Array<{ mappingId: number; batchId: number; termId: number; yearId: number; startDate: string; endDate: string; indexId: number }>) {
    const resp = await myAxios.post(`${this.baseUrl}/exam-dates/save`, { rows });
    return resp.data as any;
  }

  // ── Program Config (standardTestConfig + merge/consolidation rules) ──

  async getProgramConfig(programId: number, yearId: number) {
    const resp = await myAxios.get(`${this.baseUrl}/config`, { params: { programId, yearId } });
    return (resp.data as any)?.data;
  }

  async saveProgramConfig(programId: number, yearId: number, config: any) {
    const resp = await myAxios.post(`${this.baseUrl}/config`, { programId, yearId, config });
    return resp.data as any;
  }

  // ── DVS Patterns ──

  async getDvsPatterns(programId: number, yearId: number, termId: number, categoryId: number) {
    const resp = await myAxios.get(`${this.baseUrl}/dvs-patterns`, { params: { programId, yearId, termId, categoryId } });
    return (resp.data as any)?.data;
  }

  async saveDvsPattern(programId: number, yearId: number, termId: number, categoryId: number, dvsPatternIds: string[]) {
    const resp = await myAxios.post(`${this.baseUrl}/dvs-patterns/save`, { programId, yearId, termId, categoryId, dvsPatternIds });
    return resp.data as any;
  }

  // ── Test Timetable (schedule tests per term+category) ──

  async getTestTimetable(programId: number, termId: number, categoryId: number, yearId: number) {
    const resp = await myAxios.post(`${this.baseUrl}/timetable`, { programId, termId, categoryId, yearId });
    return (resp.data as any)?.data;
  }

  async updateTestTimetable(data: {
    templateId: number; name: string; remarks?: string;
    tests: Array<{ testId: number; testDate: string; startTime?: string; endTime?: string; remarks?: string }>;
    batchIds?: number[];
  }) {
    const resp = await myAxios.post(`${this.baseUrl}/timetable/update`, data);
    return resp.data as any;
  }

  async saveTestTimetable(data: {
    programId: number; termId: number; yearId: number; categoryId: number;
    name: string; remarks?: string;
    tests: Array<{ testId: number; testDate: string; startTime?: string; endTime?: string; remarks?: string }>;
    batchIds?: number[];
  }) {
    const resp = await myAxios.post(`${this.baseUrl}/timetable/save`, data);
    return resp.data as any;
  }

  // ── Test Data Entry Monitoring ──

  async getDataEntryStatus(programId: number, yearId: number): Promise<Record<string, string>> {
    const resp = await myAxios.get(`${this.baseUrl}/data-entry-status`, { params: { programId, yearId } });
    return (resp.data as any)?.data || {};
  }

  async getDataEntryMonitoring(yearId: number, termId: number, categoryId: number) {
    const resp = await myAxios.get(`${this.baseUrl}/data-entry-monitoring`, { params: { yearId, termId, categoryId } });
    return (resp.data as any)?.data || [];
  }

  async openDataEntry(programId: number, yearId: number, termId: number, categoryId: number) {
    const resp = await myAxios.post(`${this.baseUrl}/data-entry-open`, { programId, yearId, termId, categoryId });
    return resp.data as any;
  }

  async freezeDataEntry(ids: number[], freezeDate: string) {
    const resp = await myAxios.post(`${this.baseUrl}/data-entry-freeze`, { ids, freezeDate });
    return resp.data as any;
  }

  /**
   * Create standard tests for a program — either a single term or all terms.
   * Pass `termId` to scope to one term; omit for all terms in the program.
   */
  async createStandardTests(
    programId: number,
    yearId: number,
    termId?: number,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Promise<{ success: boolean; summary?: { created: number; skipped: number }; message?: string; skipped?: Array<{ name: string; reason: string }> }> {
    const body: Record<string, number> = { programId, yearId };
    if (termId) body.termId = termId;
    const response = await myAxios.post(`${this.baseUrl}/standard-tests`, body);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return response.data as any;
  }
}

export default new TestStructureService();
